import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, RefreshCw, Trash2, RotateCcw, Search, Upload, Archive, History } from 'lucide-react';
import { authFetch, authJsonFetch } from '../lib/api';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// ============================================================================
// Tab 1 — Trash (soft-deleted searches, restorable in one click)
// ============================================================================

function TrashTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/export-data/saved-searches/trash?_t=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setRows(data.saved_searches || []);
    } catch (e) {
      toast.error(`Failed to load trash: ${e.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const restore = async (sid, name) => {
    if (!window.confirm(`Restore "${name}"?`)) return;
    const r = await authJsonFetch(`${API}/export-data/saved-searches/${sid}/restore`, { method: 'POST' });
    if (r.ok) {
      toast.success(`Restored "${name}"`);
      load();
    } else {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || `Restore failed`);
    }
  };

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Soft-deleted saved searches. Restore brings them back to the live list.
        </p>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="trash-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">Trash is empty. 🎉</p>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {rows.map((s) => (
            <div key={s.search_id} className="border rounded-md p-3 text-sm bg-muted/30 flex items-start gap-2"
                 data-testid={`trash-row-${s.search_id}`}>
              <Trash2 className="w-4 h-4 mt-0.5 text-rose-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium">{s.name}</p>
                <p className="text-xs text-muted-foreground">
                  Deleted by <strong>{s.deleted_by_email || '?'}</strong> on {fmt(s.deleted_at)}
                </p>
                {s.years?.length > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-1">Years: {s.years.join(', ')}</p>
                )}
              </div>
              <Button
                size="sm" variant="outline"
                onClick={() => restore(s.search_id, s.name)}
                data-testid={`trash-restore-${s.search_id}`}
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Tab 2 — Audit log (who did what)
// ============================================================================

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actionFilter !== 'all') params.set('action', actionFilter);
      params.set('limit', '200');
      params.set('_t', String(Date.now()));
      const r = await authFetch(`${API}/export-data/saved-searches/audit?${params}`, { cache: 'no-store' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setRows(data.audit || []);
    } catch (e) {
      toast.error(`Failed to load audit: ${e.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [actionFilter]);

  useEffect(() => { load(); }, [load]);

  const actionColour = {
    created:  'bg-emerald-100 text-emerald-800',
    updated:  'bg-blue-100 text-blue-800',
    deleted:  'bg-rose-100 text-rose-800',
    restored: 'bg-amber-100 text-amber-800',
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Every create / update / delete / restore event on a saved search. Lockable audit trail.
        </p>
        <div className="flex items-center gap-2">
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="audit-action-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="created">Created</SelectItem>
              <SelectItem value="updated">Updated</SelectItem>
              <SelectItem value="deleted">Deleted</SelectItem>
              <SelectItem value="restored">Restored</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">No audit entries.</p>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {rows.map((r) => (
            <div key={r.audit_id} className="border rounded-md p-3 text-sm" data-testid={`audit-row-${r.audit_id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <Badge className={`text-[10px] ${actionColour[r.action] || 'bg-muted'}`}>{r.action}</Badge>
                  <span className="font-medium">{r.search_name || '(deleted)'}</span>
                </div>
                <span className="text-[11px] text-muted-foreground">{fmt(r.at)}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                by <strong>{r.by_email}</strong> ({r.by_name || '?'}) — search_id <code className="text-[10px]">{r.search_id}</code>
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Tab 3 — Restore from backup zip (handles both backup_* and backup_emails_*)
// ============================================================================

function RestoreFromBackupTab() {
  const [file, setFile] = useState(null);
  const [collections, setCollections] = useState([]);
  const [chosenCol, setChosenCol] = useState('');
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [keyField, setKeyField] = useState('');
  const [mode, setMode] = useState('skip_if_exists');
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('idle'); // idle | inspected | preview | applied

  const reset = () => {
    setFile(null); setCollections([]); setChosenCol(''); setRows([]);
    setSearch(''); setSelectedIds(new Set()); setKeyField(''); setMode('skip_if_exists');
    setStage('idle');
  };

  const inspect = async () => {
    if (!file) {
      toast.error('Pick a backup zip first.');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/admin/restore-from-backup/inspect`, {
        method: 'POST', body: fd, credentials: 'include',
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setCollections(data.collections || []);
      setStage('inspected');
    } catch (e) {
      toast.error(`Inspect failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const preview = async () => {
    if (!chosenCol) {
      toast.error('Pick a collection.');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('collection', chosenCol);
      if (search) fd.append('search', search);
      const r = await fetch(`${API}/admin/restore-from-backup/preview`, {
        method: 'POST', body: fd, credentials: 'include',
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setRows(data.rows || []);
      // Default-pick a reasonable key_field based on collection name
      const defaultKey = (
        chosenCol === 'saved_exports' ? 'search_id' :
        chosenCol === 'organizations' ? 'org_id' :
        chosenCol === 'contacts' ? 'contact_id' :
        chosenCol === 'sales' ? 'sales_id' :
        chosenCol === 'email_attachments' ? 'attachment_id' :
        ''
      );
      setKeyField(defaultKey);
      setStage('preview');
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (selectedIds.size === 0) {
      toast.error('Tick at least one row to restore.');
      return;
    }
    if (!keyField) {
      toast.error('Set the primary-key field name.');
      return;
    }
    if (!window.confirm(
      `Restore ${selectedIds.size} row(s) into '${chosenCol}' (mode: ${mode})?\nThis will modify the LIVE database.`
    )) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('collection', chosenCol);
      fd.append('key_field', keyField);
      fd.append('mode', mode);
      fd.append('ids_json', JSON.stringify([...selectedIds]));
      const r = await fetch(`${API}/admin/restore-from-backup/apply`, {
        method: 'POST', body: fd, credentials: 'include',
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      toast.success(`Inserted ${data.inserted}, overwritten ${data.overwritten}, skipped ${data.skipped?.length || 0}`);
      setStage('applied');
    } catch (e) {
      toast.error(`Apply failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = () => {
    if (!rows[0]) return;
    const idAttr = keyField || Object.keys(rows[0])[0];
    const allIds = rows.map(r => String(r[idAttr])).filter(Boolean);
    if (allIds.every(i => selectedIds.has(i))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  return (
    <div className="space-y-4">
      <div className="border rounded-md p-3 text-xs bg-amber-50 border-amber-200 text-amber-900">
        <strong>How it works:</strong> Upload one of our backup zips (
        <code>backup_*.zip</code> or <code>backup_emails_*.zip</code>),
        pick a collection, preview the rows, tick the ones you want,
        then Apply. <strong>skip_if_exists</strong> never overwrites
        live data; switch to <strong>overwrite</strong> only if you're
        sure the backup version should win.
      </div>

      <div className="flex items-center gap-2">
        <Input
          type="file" accept=".zip"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setStage('idle'); }}
          className="max-w-md"
          data-testid="restore-file-input"
        />
        <Button onClick={inspect} disabled={!file || loading} data-testid="restore-inspect-btn">
          <Upload className="w-3.5 h-3.5 mr-1" /> Inspect zip
        </Button>
        {stage !== 'idle' && (
          <Button variant="outline" onClick={reset} data-testid="restore-reset-btn">Reset</Button>
        )}
      </div>

      {collections.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            Collections inside <code>{file?.name}</code>:
          </p>
          <div className="flex flex-wrap gap-2">
            {collections.map(c => (
              <button
                key={c.collection}
                disabled={c.blocked}
                onClick={() => setChosenCol(c.collection)}
                className={`px-2 py-1 text-xs rounded border ${
                  chosenCol === c.collection ? 'border-primary bg-primary text-primary-foreground' :
                  c.blocked ? 'opacity-50 cursor-not-allowed' :
                  'hover:bg-muted'
                }`}
                data-testid={`restore-col-${c.collection}`}
              >
                {c.collection} <span className="opacity-70">({c.row_count})</span>
                {c.blocked && ' 🔒'}
              </button>
            ))}
          </div>
        </div>
      )}

      {chosenCol && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search rows (optional)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-md"
              data-testid="restore-search-input"
            />
            <Button onClick={preview} disabled={loading} data-testid="restore-preview-btn">
              <Search className="w-3.5 h-3.5 mr-1" /> Preview rows
            </Button>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Key field:</span>
            <Input
              value={keyField}
              onChange={(e) => setKeyField(e.target.value)}
              placeholder="e.g. search_id, org_id"
              className="max-w-xs h-8 text-xs"
              data-testid="restore-key-field"
            />
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="restore-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="skip_if_exists">Skip if exists (safe)</SelectItem>
                <SelectItem value="overwrite">Overwrite (danger)</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={toggleAll}>Toggle all</Button>
            <Button onClick={apply} disabled={selectedIds.size === 0 || loading} data-testid="restore-apply-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Apply ({selectedIds.size})
            </Button>
          </div>
          <div className="max-h-[400px] overflow-auto border rounded-md">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-background border-b">
                <tr>
                  <th className="p-2 text-left w-8"></th>
                  {Object.keys(rows[0]).slice(0, 5).map(k => (
                    <th key={k} className="p-2 text-left font-medium">{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const idAttr = keyField || Object.keys(r)[0];
                  const id = String(r[idAttr] || idx);
                  const selected = selectedIds.has(id);
                  return (
                    <tr key={id}
                        className={`border-b cursor-pointer ${selected ? 'bg-primary/10' : 'hover:bg-muted/40'}`}
                        onClick={() => {
                          const next = new Set(selectedIds);
                          if (next.has(id)) next.delete(id); else next.add(id);
                          setSelectedIds(next);
                        }}
                        data-testid={`restore-row-${id}`}
                    >
                      <td className="p-2"><input type="checkbox" checked={selected} readOnly /></td>
                      {Object.keys(rows[0]).slice(0, 5).map(k => (
                        <td key={k} className="p-2 max-w-[220px] truncate" title={JSON.stringify(r[k])}>
                          {typeof r[k] === 'object' ? JSON.stringify(r[k]) : String(r[k] ?? '')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main panel
// ============================================================================

const TABS = [
  { id: 'trash',   label: 'Trash',          icon: Trash2,  Component: TrashTab },
  { id: 'audit',   label: 'Audit Log',      icon: History, Component: AuditTab },
  { id: 'restore', label: 'Restore from Backup', icon: Archive, Component: RestoreFromBackupTab },
];

export default function SavedSearchAuditPanel() {
  const [active, setActive] = useState('trash');
  return (
    <Card data-testid="saved-search-audit-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Archive className="w-5 h-5" />
          Saved Search Recovery
        </CardTitle>
        <CardDescription>
          Restore accidentally-deleted saved searches, audit who did what, and pull rows out of any backup zip.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={active} onValueChange={setActive}>
          <TabsList className="grid grid-cols-3 w-full">
            {TABS.map(t => (
              <TabsTrigger key={t.id} value={t.id} data-testid={`saved-search-audit-tab-${t.id}`} className="text-xs">
                <t.icon className="w-3 h-3 mr-1" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map(t => (
            <TabsContent key={t.id} value={t.id} className="mt-4">
              <t.Component />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
